import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => {
  const n = num(value);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${n < 0 ? '-' : ''}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${n < 0 ? '-' : ''}$${(abs / 1e6).toFixed(1)}MM`;
  if (abs >= 1000) return `${n < 0 ? '-' : ''}$${(abs / 1000).toFixed(1)}K`;
  return `${n < 0 ? '-' : ''}$${abs.toFixed(0)}`;
};

const componentAmount = (component) =>
  num(component.amount ?? component.value ?? component.variance ?? component.delta);

export const buildCumulativeWaterfall = (bridgePayload = {}) => {
  const components = Array.isArray(bridgePayload.components) ? bridgePayload.components : [];
  const total =
    bridgePayload.total !== undefined
      ? num(bridgePayload.total)
      : components.reduce((sum, component) => sum + componentAmount(component), 0) + num(bridgePayload.residual);

  let cumulative = 0;
  const rows = [
    {
      key: 'base',
      label: bridgePayload.referenceLabel || 'Reference',
      start: 0,
      end: 0,
      base: 0,
      magnitude: 0,
      kind: 'bookend',
      source: 'reference'
    }
  ];

  components.forEach((component, index) => {
    const amount = componentAmount(component);
    const start = cumulative;
    const end = cumulative + amount;
    rows.push({
      key: component.id || `component-${index}`,
      label: component.label || component.component_label || component.componentLabel || component.type || `Component ${index + 1}`,
      start,
      end,
      base: Math.min(start, end),
      magnitude: Math.abs(amount),
      amount,
      kind: amount < 0 ? 'decrease' : 'increase',
      source: component.calcInputs?.source || component.calc_inputs?.source || component.source || 'calculation',
      calcInputs: component.calcInputs || component.calc_inputs || {}
    });
    cumulative = end;
  });

  if (bridgePayload.residual !== undefined && Math.abs(num(bridgePayload.residual)) > 0.0001) {
    const amount = num(bridgePayload.residual);
    const start = cumulative;
    const end = cumulative + amount;
    rows.push({
      key: 'residual',
      label: 'Residual',
      start,
      end,
      base: Math.min(start, end),
      magnitude: Math.abs(amount),
      amount,
      kind: amount < 0 ? 'decrease' : 'increase',
      source: 'tie-out residual',
      calcInputs: bridgePayload.ties || {}
    });
    cumulative = end;
  }

  rows.push({
    key: 'total',
    label: bridgePayload.totalLabel || 'Variance',
    start: 0,
    end: total,
    base: Math.min(0, total),
    magnitude: Math.abs(total),
    amount: total,
    kind: 'bookend',
    source: 'bridge total'
  });

  return rows;
};

const tooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const inputs = row.calcInputs || {};
  const inputBits = Object.entries(inputs)
    .slice(0, 5)
    .map(([key, value]) => `${key}: ${typeof value === 'object' ? JSON.stringify(value) : value}`);
  return (
    <div
      style={{
        border: '1px solid var(--ace-line)',
        borderRadius: 14,
        background: 'var(--ace-panel-strong)',
        color: 'hsl(var(--foreground))',
        padding: '10px 12px',
        maxWidth: 280
      }}
    >
      <strong style={{ display: 'block', marginBottom: 4 }}>{row.label}</strong>
      <div>{money(row.end - row.start)}</div>
      <small style={{ color: 'hsl(var(--muted-foreground))' }}>Source: {row.source}</small>
      {inputBits.length > 0 && (
        <div style={{ marginTop: 6, display: 'grid', gap: 2, fontSize: 11 }}>
          {inputBits.map((bit) => (
            <span key={bit}>{bit}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default function BridgeWaterfall({ bridge, height = 260 }) {
  const rows = useMemo(() => buildCumulativeWaterfall(bridge || {}), [bridge]);

  if (!bridge) {
    return <div style={{ color: 'hsl(var(--muted-foreground))' }}>Select a row to load bridge detail.</div>;
  }

  if (!rows.length) {
    return <div style={{ color: 'hsl(var(--muted-foreground))' }}>No bridge components returned.</div>;
  }

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart data={rows} margin={{ top: 16, right: 18, left: 8, bottom: 42 }}>
          <CartesianGrid stroke="var(--ace-line)" vertical={false} />
          <XAxis
            dataKey="label"
            interval="preserveStartEnd"
            minTickGap={12}
            angle={-24}
            textAnchor="end"
            height={58}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
          />
          <YAxis tickFormatter={money} tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
          <Tooltip content={tooltip} cursor={{ fill: 'color-mix(in srgb, var(--ace-line) 30%, transparent)' }} />
          <Bar dataKey="base" stackId="float" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="magnitude" stackId="float" radius={[0, 0, 0, 0]} isAnimationActive={false}>
            {rows.map((row) => (
              <Cell
                key={row.key}
                fill={
                  row.kind === 'bookend'
                    ? 'color-mix(in srgb, hsl(var(--foreground)) 55%, transparent)'
                    : row.kind === 'decrease'
                      ? 'var(--ace-negative)'
                      : 'var(--ace-positive)'
                }
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
